import { DownloadOutlined } from '@ant-design/icons';
import { PERMISSIONS } from '@nks/shared';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Flex, Segmented, Tag, Typography, message } from 'antd';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ListPage } from '../../components/ListPage';
import { ApiError } from '../../lib/api-client';
import { formatDate, formatDateTime } from '../../lib/format';
import { useHasPermission } from '../auth/session';
import { downloadCsv } from './csv';
import { exportRemindersCsv, listReminders, type ListRemindersParams, type Reminder } from './api';
import { DismissReminderDrawer, SendReminderDrawer } from './ReminderDrawers';

const { Text } = Typography;

type View = 'due' | 'sent' | 'all';

const VIEW_PARAMS: Record<View, ListRemindersParams> = {
  due: { status: 'DUE' },
  sent: { status: 'SENT' },
  all: {},
};

const bike = (r: Reminder): string => r.unit.plateNo ?? r.unit.chassisNo;
const sendable = (r: Reminder): boolean => r.status === 'SCHEDULED' || r.status === 'DUE';

/** Service reminders: what is due, who has been told, and the WhatsApp send. */
export function RemindersPage(): ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const canSend = useHasPermission(PERMISSIONS.REMINDER_SEND);
  const [view, setView] = useState<View>('due');
  const [acting, setActing] = useState<{ kind: 'send' | 'dismiss'; reminder: Reminder } | null>(null);

  const params = VIEW_PARAMS[view];
  const { data, isFetching, error, fetchNextPage, hasNextPage } = useInfiniteQuery({
    queryKey: ['reminders', view],
    queryFn: ({ pageParam }) => listReminders({ ...params, ...(pageParam !== undefined ? { cursor: pageParam } : {}) }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => (last.hasMore && last.nextCursor !== null ? last.nextCursor : undefined),
  });
  const rows = data?.pages.flatMap((p) => p.items) ?? [];

  const emptyFor: Record<View, string> = {
    due: t('serviceDesk.reminders.emptyDue'),
    sent: t('serviceDesk.reminders.emptySent'),
    all: t('serviceDesk.reminders.emptyAll'),
  };

  const actions = (row: Reminder): ReactNode =>
    canSend && sendable(row) ? (
      <Flex gap={8} wrap>
        <Button
          type="primary"
          size="large"
          onClick={(e) => {
            e.stopPropagation();
            setActing({ kind: 'send', reminder: row });
          }}
        >
          {t('serviceDesk.reminders.send')}
        </Button>
        <Button
          size="large"
          onClick={(e) => {
            e.stopPropagation();
            setActing({ kind: 'dismiss', reminder: row });
          }}
        >
          {t('serviceDesk.reminders.dismiss')}
        </Button>
      </Flex>
    ) : null;

  const status = (r: Reminder): ReactNode => (
    <Tag color={r.status === 'DUE' ? 'warning' : r.status === 'SENT' ? 'success' : undefined}>
      {t(`serviceDesk.reminderStatus.${r.status}`, { defaultValue: r.status })}
    </Tag>
  );

  const rule = (r: Reminder): string => t(`serviceDesk.reminderRule.${r.ruleCode}`, { defaultValue: r.ruleCode });
  const mileage = (r: Reminder): string => (r.dueMileage === null ? '—' : r.dueMileage.toLocaleString('en-MY'));

  const close = (): void => { setActing(null); };
  const done = (): void => {
    setActing(null);
    void queryClient.invalidateQueries({ queryKey: ['reminders'] });
    void queryClient.invalidateQueries({ queryKey: ['follow-ups'] });
  };

  return (
    <>
      <Flex vertical gap={12}>
        <Segmented<View>
          value={view}
          onChange={setView}
          size="large"
          block
          options={[
            { value: 'due', label: t('serviceDesk.reminders.views.due') },
            { value: 'sent', label: t('serviceDesk.reminders.views.sent') },
            { value: 'all', label: t('serviceDesk.reminders.views.all') },
          ]}
        />
        {error !== null && (
          <Text type="danger">{error instanceof ApiError ? error.message : t('serviceDesk.reminders.loadFailed')}</Text>
        )}
        <ListPage<Reminder>
          title={t('serviceDesk.reminders.title')}
          loading={isFetching}
          emptyDescription={emptyFor[view]}
          actions={
            <Button
              icon={<DownloadOutlined />}
              onClick={() =>
                void downloadCsv(t, () => exportRemindersCsv(params), `service-reminders-${view}.csv`, 'serviceDesk.reminders.exportFailed')
              }
            >
              {t('serviceDesk.reminders.exportCsv')}
            </Button>
          }
          columns={[
            { title: t('serviceDesk.reminders.col.due'), key: 'due', width: 120, render: (_: unknown, r) => formatDate(r.dueDate) },
            { title: t('serviceDesk.reminders.col.customer'), key: 'customer', render: (_: unknown, r) => r.customer.name },
            { title: t('serviceDesk.reminders.col.bike'), key: 'bike', width: 130, render: (_: unknown, r) => bike(r) },
            { title: t('serviceDesk.reminders.col.rule'), key: 'rule', width: 150, render: (_: unknown, r) => rule(r) },
            { title: t('serviceDesk.reminders.col.mileage'), key: 'km', width: 100, render: (_: unknown, r) => mileage(r) },
            {
              title: t('serviceDesk.reminders.col.status'),
              key: 'status',
              width: 160,
              render: (_: unknown, r) => (
                <Flex vertical gap={2}>
                  {status(r)}
                  {r.sentAt !== null && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {t('serviceDesk.reminders.sentAt', { at: formatDateTime(r.sentAt) })}
                    </Text>
                  )}
                </Flex>
              ),
            },
            { title: '', key: 'action', width: 300, render: (_: unknown, r) => actions(r) },
          ]}
          rows={rows}
          rowKey={(row) => row.uid}
          hasMore={hasNextPage}
          onLoadMore={() => void fetchNextPage()}
          renderMobileRow={(row) => (
            <Flex vertical gap={6}>
              <Flex justify="space-between" align="center" gap={8}>
                <Text strong>{row.customer.name}</Text>
                {status(row)}
              </Flex>
              <Text>
                {rule(row)} · {bike(row)}
              </Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {formatDate(row.dueDate)}
                {row.dueMileage !== null ? ` · ${mileage(row)} km` : ''}
                {row.sentAt !== null ? ` · ${t('serviceDesk.reminders.sentAt', { at: formatDateTime(row.sentAt) })}` : ''}
              </Text>
              {actions(row)}
            </Flex>
          )}
        />
      </Flex>

      {acting?.kind === 'send' && (
        <SendReminderDrawer key={acting.reminder.uid} open reminder={acting.reminder} onClose={close} onDone={done} />
      )}
      {acting?.kind === 'dismiss' && (
        <DismissReminderDrawer
          key={acting.reminder.uid}
          open
          reminder={acting.reminder}
          onClose={close}
          onDone={() => {
            done();
            void message.success(t('serviceDesk.drawer.saved'));
          }}
        />
      )}
    </>
  );
}
